import React, { useState, useEffect, useRef } from "react";
import "./select-input.css";
import IconSvg from "../icon-svg/icon-svg";
import { AppIcon } from "../icon-svg/app-icon.model";
import SearchInputText from "../search-input-text/search-input-text";

type Option = {
  value: string;
  label: string;
};

type SelectInputProps = {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  value: Option | null;
  onValueChange: (option: Option) => void;
  error?: string;
  disabled?: boolean;
  showSearch?: boolean;
  visibleOptions: Option[];
  icon?: AppIcon;
  translate?: (str: string) => string;
};

const SelectInput: React.FC<SelectInputProps> = ({
  name,
  label,
  required = false,
  placeholder = "",
  value,
  onValueChange,
  error = "",
  disabled = false,
  showSearch = false,
  visibleOptions = [],
  icon = AppIcon.CHEVRON_DOWN,
  translate = (s) => s,
}) => {
  const [isDirty, setIsDirty] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [optionSearch, setOptionSearch] = useState("");
  const inputRef = useRef<HTMLDivElement | null>(null);

  const toggleOptions = () => {
    setShowOptions((prev) => !prev);
    setIsDirty(true);
  };

  const filteredOptions = visibleOptions.filter((opt) =>
    opt.label.toLowerCase().includes(optionSearch.toLowerCase())
  );

  const handleValueChange = (option: Option) => {
    onValueChange(option);
    setShowOptions(false);
    setOptionSearch("");
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`select-input-container ${error && isDirty ? "error" : ""}`}
      ref={inputRef}
    >
      <div className="select-input">
        {label && (
          <label htmlFor={name} className="select-input-label">
            {translate(label)}
            {required ? "*" : ""}
          </label>
        )}

        <div className="input-container">
          <input
            type="text"
            id={name}
            name={name}
            placeholder={translate(placeholder)}
            value={value?.label || ""}
            onClick={toggleOptions}
            className="input-field"
            required={required}
            disabled={disabled}
            readOnly
          />
          <IconSvg
            path={icon}
            className="toggle-icon-svg"
            onClick={toggleOptions}
          />
        </div>
      </div>

      {error && isDirty && (
        <label className="error-message">{translate(error)}</label>
      )}

      {showOptions && (
        <div className="options-list-container">
          {showSearch && (
            <div className="options-list-header">
              <SearchInputText
                label=""
                value={optionSearch}
                onValueChange={setOptionSearch}
                onSearch={() => {}}
              />
            </div>
          )}
          <div className="options-list">
            {filteredOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleValueChange(option)}
                className={`option-button ${
                  option.value === value?.value ? "selected" : ""
                }`}
              >
                {translate(option.label)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectInput;
